/* MW · Assistance — floating concierge behaviour.
   Non-modal popover: it never locks the page, but while open it keeps Tab
   inside itself, closes on Escape or an outside press, and hands focus back
   to the launcher. Closed, it is removed from the tab order with `inert`. */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* Bottom-anchored chrome the launcher should sit above: the customer
     privacy banner on the storefront, the preview bar in the theme editor. */
  var CHROME = ['#shopify-pc__banner', '.shopify-pc__banner', '#PBarNextFrameWrapper'];

  /* Dismissing the prompt is remembered for the tab, not the browser, so it
     stays a one-off without following anyone around. Storage can throw in
     private modes, where forgetting is the harmless outcome. */
  var NUDGE_KEY = 'mw-assist-nudge-dismissed';

  function nudgeDismissed() {
    try {
      return window.sessionStorage.getItem(NUDGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function rememberNudgeDismissed() {
    try {
      window.sessionStorage.setItem(NUDGE_KEY, '1');
    } catch (e) {
      /* no storage, no memory - the prompt simply returns next page */
    }
  }

  function Assistance(root) {
    this.root = root;
    this.panel = root.querySelector('[data-mw-assist-panel]');
    this.launcher = root.querySelector('[data-mw-assist-launcher]');
    this.dismiss = root.querySelector('[data-mw-assist-dismiss]');

    if (!this.panel || !this.launcher) return;

    this.open = false;
    this.onDocumentPointer = this.handleDocumentPointer.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);

    this.launcher.addEventListener('click', this.toggle.bind(this));
    if (this.dismiss) this.dismiss.addEventListener('click', this.close.bind(this, true));

    /* Following an action link should leave the panel closed behind it. */
    this.panel.addEventListener('click', function (event) {
      if (event.target.closest('.mw-assist__row')) this.close(false);
    }.bind(this));

    this.setOpen(false);
    this.measureChrome();
    this.watchChrome();
    this.setupNudge();
  }

  Assistance.prototype.setupNudge = function () {
    this.nudge = this.root.querySelector('[data-mw-assist-nudge]');
    if (!this.nudge || nudgeDismissed()) return;

    var nudge = this.nudge;
    var self = this;

    nudge.querySelector('[data-mw-assist-nudge-close]')
      .addEventListener('click', function () { self.hideNudge(); });

    /* The words themselves are the invitation, so they open the panel. */
    nudge.querySelector('[data-mw-assist-nudge-open]')
      .addEventListener('click', function () { self.toggle(); });

    var delay = parseFloat(nudge.getAttribute('data-mw-assist-nudge-delay'));
    if (!(delay > 0)) delay = 8;

    this.nudgeTimer = window.setTimeout(function () {
      self.nudgeTimer = null;
      if (self.open || nudgeDismissed()) return;
      nudge.hidden = false;
      /* Leave display:none behind before the transition is asked to run. */
      void nudge.offsetWidth;
      self.root.classList.add('is-nudging');
    }, delay * 1000);
  };

  Assistance.prototype.hideNudge = function () {
    if (this.nudgeTimer) {
      window.clearTimeout(this.nudgeTimer);
      this.nudgeTimer = null;
    }
    if (!this.nudge) return;
    rememberNudgeDismissed();
    if (this.nudge.hidden) return;

    this.root.classList.remove('is-nudging');

    /* Out of the tab order once it has faded, not before. Dismissing from the
       keyboard leaves focus on a control that is about to vanish, so hand it
       to the launcher rather than letting it fall back to the document. */
    var nudge = this.nudge;
    var launcher = this.launcher;
    window.setTimeout(function () {
      if (nudge.contains(document.activeElement)) launcher.focus();
      nudge.hidden = true;
    }, 500);
  };

  Assistance.prototype.setOpen = function (open) {
    this.open = open;
    this.root.classList.toggle('is-open', open);
    this.launcher.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (open) {
      this.panel.removeAttribute('inert');
      document.addEventListener('keydown', this.onKeydown, true);
      document.addEventListener('pointerdown', this.onDocumentPointer, true);
    } else {
      /* Blur anything inside before hiding, so focus never sits on inert content. */
      if (this.panel.contains(document.activeElement)) this.launcher.focus();
      this.panel.setAttribute('inert', '');
      document.removeEventListener('keydown', this.onKeydown, true);
      document.removeEventListener('pointerdown', this.onDocumentPointer, true);
    }
  };

  Assistance.prototype.toggle = function () {
    if (this.open) {
      this.close(true);
      return;
    }

    /* Opening answers the prompt, so it should not be waiting afterwards. */
    this.hideNudge();

    this.setOpen(true);
    this.measureChrome();

    var first = this.panel.querySelector(FOCUSABLE);
    if (first) first.focus();
  };

  Assistance.prototype.close = function (returnFocus) {
    if (!this.open) return;
    this.setOpen(false);
    if (returnFocus) this.launcher.focus();
  };

  Assistance.prototype.handleKeydown = function (event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.stopPropagation();
      this.close(true);
      return;
    }

    if (event.key !== 'Tab') return;

    var items = Array.prototype.filter.call(
      this.panel.querySelectorAll(FOCUSABLE),
      function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0;
      }
    );
    if (!items.length) return;

    /* The launcher is the last stop in the loop, so the close control stays
       reachable in both directions without trapping the page. */
    items.push(this.launcher);

    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && (active === first || !this.root.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  Assistance.prototype.handleDocumentPointer = function (event) {
    if (this.root.contains(event.target)) return;
    this.close(false);
  };

  /* Lift the widget clear of any bottom-anchored chrome that is on screen.
     Only chrome that both rests on the bottom edge and shares the launcher's
     column can actually collide: the Shopify consent banner, for instance, is
     bottom-anchored but centred and narrower than the viewport, so it passes
     under nothing and must not move us. */
  Assistance.prototype.measureChrome = function () {
    var lift = 0;
    var launcher = this.launcher.getBoundingClientRect();

    for (var i = 0; i < CHROME.length; i++) {
      var el = document.querySelector(CHROME[i]);
      if (!el) continue;

      var style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      if (style.position !== 'fixed' && style.position !== 'sticky') continue;

      var box = el.getBoundingClientRect();
      if (!box.height) continue;
      if (window.innerHeight - box.bottom > 4) continue;
      if (box.right <= launcher.left || box.left >= launcher.right) continue;

      lift = Math.max(lift, box.height + 12);
    }

    /* Anything approaching full height is a modal covering the page, not a
       bar to step over; never push the launcher off-screen chasing it. */
    if (lift > window.innerHeight * 0.45) lift = 0;

    this.root.style.setProperty('--mw-assist-lift', lift ? lift + 'px' : '0rem');
  };

  Assistance.prototype.watchChrome = function () {
    var measure = this.measureChrome.bind(this);
    var scheduled = false;
    var schedule = function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        measure();
        watchElements();
      });
    };

    window.addEventListener('resize', schedule, { passive: true });

    if (!('MutationObserver' in window)) {
      window.setTimeout(measure, 1200);
      return;
    }

    /* Two observers, deliberately narrow. The first catches chrome being
       inserted or removed. The second watches only the chrome elements
       themselves, because Shopify hides its consent banner rather than
       removing it -- without this the launcher would stay lifted over a
       banner that is no longer on screen. Watching attributes across the
       whole body instead would re-measure on every animation frame of an
       already animation-heavy theme. */
    new MutationObserver(schedule).observe(document.body, { childList: true });

    var attrs = new MutationObserver(schedule);
    var watched = [];
    function watchElements() {
      for (var i = 0; i < CHROME.length; i++) {
        var el = document.querySelector(CHROME[i]);
        if (!el || watched.indexOf(el) !== -1) continue;
        watched.push(el);
        attrs.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      }
    }

    watchElements();
    window.setTimeout(function () { measure(); watchElements(); }, 1200);
  };

  function init() {
    var roots = document.querySelectorAll('[data-mw-assist]');
    for (var i = 0; i < roots.length; i++) new Assistance(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
